import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
  Col,
} from 'components';
import PropTypes from 'prop-types';
import { globalPropTypes } from 'helpers';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import checkPermissions from '../../helpers/checkPermissions';

const statusOptions = [
  { value: 'Pending', label: 'Pending' },
  { value: 'Verified', label: 'Verified' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Implemented', label: 'Implemented' },
  { value: 'Abandoned', label: 'Abandoned' },
];

const ChangeRequest = ({
  loading,
  type,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeChangeRequest,
  verifyChangeRequest,
  approveChangeRequest,
  implementChangeRequest,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  // Modal state for deletes, verify, approve/reject
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    action: null, // 'delete', 'verify', 'reject' (from verify), 'approve', 'reject' (from approve)
    target: null, // the item affected
    inProgress: false,
  });

  const isIpma = type === 'ipma';
  const isMa = type === 'ma';
  const canVerify = checkPermissions({ roles: ['ipma_verify_change_request'] }, userPermissions || []);
  const canApprove = checkPermissions({ roles: ['ipma_approve_change_request'] }, userPermissions || []);
  const canImplement = checkPermissions({ roles: ['ipma_notify_approved_change_request'] }, userPermissions || []);

  const changeRequestTypeOptions = [
    { value: 'change_in_cis', label: t('page.user.cis.changeRequests.typeChangeInCis') },
    { value: 'late_upload', label: t('page.user.cis.changeRequests.typeLateUpload') },
  ];

  const canEdit = (item) => {
    if (isMa) {
      return item.status === 'Pending';
    }
    return false;
  };

  const canDelete = (item) => {
    if (isMa) {
      return item.status === 'Pending';
    }
    return false;
  };

  const canVerifyReject = (item) => {
    if (isIpma && canVerify) {
      return item.status === 'Pending';
    }
    return false;
  };

  const canApproveReject = (item) => {
    if (isIpma && canApprove) {
      return item.status === 'Verified' || item.status === 'Refuted';
    }
    return false;
  };

  const canImplementFinal = (item) => {
    if (isIpma && canImplement) {
      return item.status === 'Approved';
    }
    return false;
  };

  useEffect(() => {
    if (document.getElementById(`${type}-cis-buttons-portal`)) {
      setPortalReady(true);
    }
  }, [type]);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  // Unified confirm modal handler
  const openConfirmModal = (action, target) => {
    setConfirmModal({
      isOpen: true,
      action,
      target,
      inProgress: false,
    });
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      action: null,
      target: null,
      inProgress: false,
    });
  };

  const handleConfirmAction = async () => {
    const { action, target } = confirmModal;
    if (!target) return;

    setConfirmModal((prev) => ({ ...prev, inProgress: true }));

    if (action === 'delete') {
      await removeChangeRequest(target.id);
      closeConfirmModal();
    } else if (action === 'verify') {
      if (verifyChangeRequest) {
        await verifyChangeRequest(target.id, { action: 'verify' });
        await loadList(); // refresh after action
      }
      closeConfirmModal();
    } else if (action === 'refute') {
      if (verifyChangeRequest) {
        await verifyChangeRequest(target.id, { action: 'refute' });
        await loadList(); // refresh after action
      }
      closeConfirmModal();
    } else if (action === 'approve' || action === 'reject') {
      if (approveChangeRequest) {
        await approveChangeRequest(target.id, { action });
        await loadList(); // refresh after action
      }
      closeConfirmModal();
    } else if (action === 'implement') {
      if (implementChangeRequest) {
        await implementChangeRequest(target.id, { action });
        await loadList(); // refresh after action
      }
      closeConfirmModal();
    }
  };

  const truncateText = (text, maxLength = 50) => {
    if (!text) return '';
    return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
  };

  const getModalHeading = () => {
    if (confirmModal.action === 'delete') {
      return t('page.user.cis.changeRequests.confirmDelete');
    }
    if (confirmModal.action === 'verify') {
      return t('page.user.cis.changeRequests.confirmVerifyTitle');
    }
    if (confirmModal.action === 'refute') {
      return t('page.user.cis.changeRequests.confirmRefuteTitle');
    }
    if (confirmModal.action === 'approve') {
      return t('page.user.cis.changeRequests.confirmApproveTitle');
    }
    if (confirmModal.action === 'reject') {
      return t('page.user.cis.changeRequests.confirmRejectTitle');
    }
    if (confirmModal.action === 'implement') {
      return t('page.user.cis.changeRequests.confirmImplementTitle');
    }
    return '';
  };

  const getModalDescription = () => {
    if (confirmModal.action === 'verify') {
      return t('page.user.cis.changeRequests.confirmVerifyText');
    }
    if (confirmModal.action === 'refute') {
      return t('page.user.cis.changeRequests.confirmRefuteText');
    }
    if (confirmModal.action === 'approve') {
      return t('page.user.cis.changeRequests.confirmApproveText');
    }
    if (confirmModal.action === 'reject') {
      return t('page.user.cis.changeRequests.confirmRejectText');
    }
    if (confirmModal.action === 'implement') {
      return t('page.user.cis.changeRequests.confirmImplementText');
    }
    return '';
  };

  console.log(items);

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.description || ''}
                  onFilter={(val) => handleColumnFilterChange('description', val)}
                  filterPlaceholder={t('page.user.cis.changeRequests.descriptionPlaceholder')}
                  label={t('page.user.cis.changeRequests.description')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.type || ''}
                  onFilter={(val) => handleColumnFilterChange('type', val)}
                  filterOptions={changeRequestTypeOptions}
                  filterPlaceholder={t('page.user.cis.changeRequests.typePlaceholder')}
                  label={t('page.user.cis.changeRequests.type')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={statusOptions}
                  filterPlaceholder={t('page.user.cis.changeRequests.statusPlaceholder')}
                  label={t('page.user.cis.changeRequests.status')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.changeRequests.createdAt')}
                  sort
                  sortDirection={getSortDirection('createdAt')}
                  onSort={(dir) => handleColumnSort('createdAt', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.changeRequests.createdBy')}
                  filter
                  filterType="input"
                  filterValue={filters.createdBy || ''}
                  onFilter={(val) => handleColumnFilterChange('createdBy', val)}
                  filterPlaceholder={t('page.user.cis.changeRequests.createdByPlaceholder')}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                <TableHeaderCell
                  label={t('page.user.cis.changeRequests.actions')}
                  labelAlign="center"
                  isAction={true}
                />
              </CisTable.StickyActionHeading>
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={6}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={6}>
                  {t('global.noResultsFound')}
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.changeRequests.description')}</Table.Label>
                      {truncateText(item.description)}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.changeRequests.type')}</Table.Label>
                      {changeRequestTypeOptions.find((x) => x.value === item.type)?.label || item.type || '—'}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.changeRequests.status')}</Table.Label>
                      {statusOptions.find((x) => x.value === item.status)?.label || item.status}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.changeRequests.createdAt')}</Table.Label>
                      {item.createdAt && moment(item.createdAt).format('DD/MM/YYYY HH:mm')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.changeRequests.createdBy')}</Table.Label>
                      {item.createdBy}
                    </CisTable.Cell>
                    <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                      <Table.Label>{t('page.user.cis.changeRequests.actions')}:</Table.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        <ButtonIcon
                          icon="file"
                          secondary
                          as={Button}
                          onClick={() => {
                            setProfile(item);
                            history.push(`/${type}/cis/change-requests/view`);
                          }}
                        />
                        {isMa && canEdit(item) && (
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/${type}/cis/change-requests/edit`);
                            }}
                          />
                        )}
                        {isIpma && canVerifyReject(item) && (
                          <>
                            <ButtonIcon
                              icon="check"
                              secondary
                              as={Button}
                              onClick={() => openConfirmModal('verify', item)}
                            />
                            <ButtonIcon
                              icon="close"
                              color="white"
                              bg="red"
                              secondary
                              as={Button}
                              onClick={() => openConfirmModal('refute', item)}
                            />
                          </>
                        )}
                        {isIpma && canApproveReject(item) && (
                          <>
                            <ButtonIcon
                              icon="check"
                              secondary
                              as={Button}
                              onClick={() => openConfirmModal('approve', item)}
                            />
                            <ButtonIcon
                              icon="close"
                              color="white"
                              bg="red"
                              secondary
                              as={Button}
                              onClick={() => openConfirmModal('reject', item)}
                            />
                          </>
                        )}
                        {isIpma && canImplementFinal(item) && (<ButtonIcon
                              icon="check"
                              secondary
                              as={Button}
                              onClick={() => openConfirmModal('implement', item)}
                            />
                        )}
                        {isMa && canDelete(item) && (
                          <ButtonIcon
                            icon="trash"
                            onClick={() => openConfirmModal('delete', item)}
                          />
                        )}
                      </Row>
                    </CisTable.StickyActionCell>
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={getModalHeading()}
        isOpen={confirmModal.isOpen && confirmModal.target?.id}
        confirm={handleConfirmAction}
        cancel={closeConfirmModal}
        onRequestClose={closeConfirmModal}
        disabled={confirmModal.inProgress}
        description={getModalDescription()}
      />
      {portalReady &&
        ReactDOM.createPortal(
          <Row style={{ alignItems: 'center', justifyContent: 'end', gap: '10px' }}>
            {isMa && (
              <Col size={{ xs: 1 / 3 }}>
                <Button secondary as={Link} to={`/${type}/cis/change-requests/create`}>
                  {t('page.user.cis.changeRequests.new')}
                </Button>
              </Col>
            )}
          </Row>,
          document.getElementById(`${type}-cis-buttons-portal`),
        )}
    </PageContent>
  );
};

ChangeRequest.propTypes = {
  loading: PropTypes.bool,
  type: PropTypes.oneOf(['ipma', 'ma']),
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeChangeRequest: PropTypes.func,
  verifyChangeRequest: PropTypes.func,
  approveChangeRequest: PropTypes.func,
  implementChangeRequest: PropTypes.func,
  userPermissions: PropTypes.array,
};

ChangeRequest.defaultProps = {
  loading: false,
  removeChangeRequest: () => {},
  verifyChangeRequest: null,
  approveChangeRequest: null,
  implementChangeRequest: null,
  userPermissions: [],
};

export default ChangeRequest;
